// SPDX-License-Identifier: AGPL-3.0-or-later

import {connectPrivyBusinessWallet} from '@app/features/auth/privy/PrivyWalletConnection';
import {findBusinessWallet} from '@app/features/auth/privy/PrivyWalletPolicy';
import {PrivyWalletRestoreLogin} from '@app/features/auth/privy/PrivyWalletRestoreLogin';
import {DaoIWallet, type InjectedWallet} from '@app/features/daoi/bridge/DaoIWallet';
import SessionManager from '@app/features/platform/state/AuthSession';
import {Button} from '@app/features/ui/button/Button';
import {
	AccountSecurityEmptyRow,
	AccountSecurityRow,
} from '@app/features/user/components/modals/tabs/account_security_tab/AccountSecurityCard';
import {useExportWallet, usePrivy, useWallets} from '@privy-io/react-auth';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

export const PrivyWalletManagement = observer(() => {
	const {ready, authenticated} = usePrivy();
	const {wallets} = useWallets();
	const {exportWallet} = useExportWallet();
	const token = SessionManager.token;
	const wallet = findBusinessWallet(wallets, DaoIWallet.boundAddress);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const run = async (action: () => Promise<void>) => {
		if (busy) return;
		setBusy(true);
		setError(null);
		try {
			await action();
		} catch {
			setError('钱包操作未完成，请重试。');
		} finally {
			setBusy(false);
		}
	};
	return (
		<>
			<AccountSecurityRow
				label="账号绑定地址"
				description={DaoIWallet.boundAddress ?? '尚未确认业务钱包'}
				data-flx="auth.privy.management-address"
			>
				<Button
					variant="secondary"
					compact
					disabled={busy || !token || !ready || !authenticated || !wallet}
					onClick={() =>
						void run(async () => {
							if (!token || !ready || !authenticated || !wallet) return;
							const provider = (await wallet.getEthereumProvider()) as InjectedWallet;
							const active = () => SessionManager.token === token;
							if (!active()) return;
							await connectPrivyBusinessWallet(provider, wallet.address, active);
						})
					}
					data-flx="auth.privy.management-recover"
				>
					恢复连接
				</Button>
			</AccountSecurityRow>
			<AccountSecurityRow
				label="备份钱包私钥"
				description="在 Privy 安全窗口中查看。请妥善离线保存，勿发送给任何人。"
				data-flx="auth.privy.management-export-row"
			>
				<Button
					variant="secondary"
					compact
					disabled={busy || !wallet || !authenticated}
					onClick={() =>
						void run(async () => {
							if (wallet) await exportWallet({address: wallet.address});
						})
					}
					data-flx="auth.privy.management-export"
				>
					导出私钥
				</Button>
			</AccountSecurityRow>
			{!authenticated && DaoIWallet.boundAddress && (
				<AccountSecurityEmptyRow data-flx="auth.privy.management-auth-required">
					<PrivyWalletRestoreLogin address={DaoIWallet.boundAddress} />
				</AccountSecurityEmptyRow>
			)}
			{error && (
				<AccountSecurityEmptyRow data-flx="auth.privy.management-error">
					<p role="alert" className="daoi-error" data-flx="auth.privy.management-error-text">
						{error}
					</p>
				</AccountSecurityEmptyRow>
			)}
		</>
	);
});
